import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from '@/lib/api/client';
import { sourceKeys } from '@/features/sources/api';

export type Automation = Schemas['ScrapeAutomationDto'];
export type Schedule = Schemas['ScrapeScheduleDto'];
export type ScheduledSource = Schemas['ScheduledSourceDto'];

// Under the sources' key: the schedule is theirs, so a source change or a run landing reads it again.
export const automationKeys = {
  all: [...sourceKeys.all, 'automation'] as const,
};

/** Whether automation is paused, by whom, and what the schedule would start and when. */
export const automationQuery = () =>
  queryOptions({
    queryKey: automationKeys.all,
    queryFn: () => unwrap(api.GET('/api/scraping/automation')),
    staleTime: 30_000,
  });

export const useAutomation = () => useQuery(automationQuery());

/** Pause stops the schedule and a scheduled run in progress; Run now keeps working. */
export function useSetAutomationPaused() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ paused, note }: { paused: boolean; note?: string | null }) =>
      unwrap(
        paused
          ? api.POST('/api/scraping/automation/pause', { body: { note: note ?? null } })
          : api.POST('/api/scraping/automation/resume'),
      ),
    onSettled: () => queryClient.invalidateQueries({ queryKey: automationKeys.all }),
  });
}
